import { EyeSlash } from '@phosphor-icons/react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { users as usersApi } from '../../api/endpoints';
import { ScreenError, ScreenLoader, SubScreen } from '../../components/layout/Screens';
import { Avatar } from '../../components/ui/Avatar';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/Bits';
import { clearScreenCache, useAsync } from '../../hooks/useAsync';
import { t } from '../../i18n';

/**
 * Les comptes masques.
 *
 * On en sort un d'ici, sans repasser par son profil. Rien d'autre n'a
 * bouge pendant qu'il etait masque : l'amitie, les messages, les demandes
 * sont restes ce qu'ils etaient, il n'y a donc rien a refaire.
 */
export function HiddenAccountsScreen() {
  const { data, loading, error, reload } = useAsync(
    () => usersApi.hiddenAccounts(),
    [],
    'comptes-masques',
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  async function afficher(userId: string) {
    setBusy(userId);
    setFailure(null);
    try {
      await usersApi.unhide(userId);
      /* Le fil et les sorties se reliront avec ses publications. */
      clearScreenCache();
      reload();
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : t.profil.masques.impossible);
    } finally {
      setBusy(null);
    }
  }

  return (
    <SubScreen title={t.profil.masques.comptesMasques}>
      {loading && <ScreenLoader />}
      {error && <ScreenError message={error} onRetry={reload} />}
      {failure && <p className="field__error">{failure}</p>}

      {data && data.length === 0 && (
        <EmptyState
          icon={<EyeSlash size={30} />}
          title={t.profil.masques.comptes.personne}
          hint={t.profil.masques.comptes.personneAide}
        />
      )}

      {data && data.length > 0 && (
        <>
          <p className="pr-blocked__intro">{t.profil.masques.comptes.explication}</p>

          {data.map((member) => (
            <div key={member.id} className="card ms-person">
              <Link to={`/app/members/${member.id}`} aria-label={member.username}>
                <Avatar user={member} size={48} />
              </Link>

              <div className="ms-person__body">
                <Link to={`/app/members/${member.id}`} className="ms-person__name">
                  {member.username}
                </Link>
                <p className="ms-person__note">{t.profil.masques.comptes.masque}</p>
              </div>

              <Button
                variant="soft"
                size="md"
                loading={busy === member.id}
                onClick={() => void afficher(member.id)}
              >
                {t.profil.masques.afficher}
              </Button>
            </div>
          ))}
        </>
      )}
    </SubScreen>
  );
}
